const get = require('lodash/get');
const find = require('lodash/find');
const filter = require('lodash/filter');
const matchesProperty = require('lodash/matchesProperty');
const values = require('lodash/values');
const session = require('@flowio/session');
const map = require('lodash/map');
const events = require('./events');
const optionsSuccess = require('./utilities/options-success');
const optionsError = require('./utilities/options-error');
const globalFeatures = require('./constants/features').default;

const eventsConstants = events.constants;
let initialized = false;
// We need to cache internally to support unit tests ability to modify content.
const cachedFeatureValues = {};

function getFeatureKeys() {
  return values(globalFeatures);
}

function getQuery(organization) {
  const globalFeatureQuery = map(globalFeatures, feature => feature).join(' or ');
  return `organization.id:${organization} or feature.key in (${globalFeatureQuery})`;
}

function init() {
  if (!initialized) {
    return new Promise((resolve, reject) => {
      events.on(events.constants.BROWSER_BUNDLE_DATA, (bundle) => {
        initialized = true;
        cachedFeatureValues.features = bundle.features;
        resolve(cachedFeatureValues.features);
      });

      events.on(events.constants.BROWSER_BUNDLE_FAILURE, (data) => {
        initialized = true;
        reject(data);
      });
    });
  }
  return Promise.resolve(cachedFeatureValues.features);
}

/** @function getFeatures
 *
 * Returns FeatureValueResult from browser buundle data.
 *
 * @param {Object} options
 * @param {Function} options.success - Callback called when features resolves successfully
 * @param {Function} options.error - Callback called when an error occurs
 *
 * references:
 * organization_session - https://docs.flow.io/type/organization-session
 */
function getFeatures(options = {}) {
  return init()
    .then((featuresResult) => {
      optionsSuccess(options, featuresResult);
      return featuresResult;
    })
    .catch((err) => {
      optionsError(options, err);
    });
}

/** @function getFeaturesByType
 *
 * Calls getFeatures and filters result by given parameter
 *
 * @param {string} featureType - valid feature_type
 * @param {Function} options.success - Callback called when features resolves successfully
 * @param {Function} options.error - Callback called when an error occurs
 *
 * references:
 * feature_type - https://app.apibuilder.io/flow/feature/0.5.44#enum-feature_type
 */
function getFeaturesByType(featureType, options) {
  return getFeatures({
    ...options,
    success: (features) => {
      const filteredByType = filter(get(features, 'values', []), { discriminator: featureType });
      optionsSuccess(options, filteredByType);
    },
    error: (error) => {
      optionsError(options, error);
    },
  });
}

function getFeature(featureKey) {
  return new Promise((resolve, reject) => {
    getFeatures({
      success: features =>
        resolve(find(features.values, feat => feat.feature.key === featureKey)),
      error: error => reject(error),
    });
  });
}

/**
 * Returns whether the feature matching the specified key in the feature value
 * results is enabled.
 * @param {string} featureKey
 * @param {FeatureValuesResult} featureValuesResult
 */
function isFeatureInResultEnabled(featureKey, featureValuesResult) {
  if (!featureValuesResult || !featureValuesResult.values || !featureValuesResult.values.length) {
    return false;
  }

  return featureValuesResult.values.some(featureValue => (
    featureValue.feature.key === featureKey
    && featureValue.discriminator === 'boolean'
    && featureValue.value === true
  ));
}

function isFeatureEnabled(featureKey) {
  return getFeature(featureKey).then((feature) => {
    if (feature) {
      return feature.value === true;
    }
    return false;
  });
}

/** @function selectElements
 *
 * @private
 * @param {string} query - A valid CSS selector
 *
 * references: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Selectors
 */
function selectElements(query) {
  const nodeList = window.document.querySelectorAll(query);
  return [].slice.call(nodeList);
}

/** @function localizeStringFeatures
 *
 * Fetch features and localize annotated DOM elements for string features
 *
 * @param {Function} options.success - Callback called when features resolves successfully
 * @param {Function} options.error - Callback called when an error occurs
 */
function localizeStringFeatures(options) {
  const experience = session.getExperience();

  if (!experience) {
    optionsSuccess(options);
    return;
  }

  const featureElements = selectElements('[flow-string-feature]');
  const localizedFeatures = [];

  getFeaturesByType('string', {
    success: (stringFeatures) => {
      featureElements.forEach((featureElement) => {
        const featureKey = featureElement.getAttribute('flow-string-feature');
        const feature = find(stringFeatures, matchesProperty('feature.key', featureKey));
        const featureValue = get(feature, 'value');

        if (featureValue) {
          // eslint-disable-next-line no-param-reassign
          featureElement.innerText = featureValue;
          localizedFeatures.push(feature);
        }
      });
      events.trigger(eventsConstants.FEATURES_LOCALIZED, localizedFeatures);
      optionsSuccess(options);
    },
    error: (error) => {
      optionsError(options, error);
    },
  });
}

/** @function localize
 *
 * Sugar for localizeStringFeatures
 *
 * @param {Function} options.success - Callback called when features resolves successfully
 * @param {Function} options.error - Callback called when an error occurs
 */
function localize(options) {
  localizeStringFeatures(options);
}

module.exports = {
  getFeatures,
  getFeature,
  getFeatureKeys,
  getQuery,
  getFeaturesByType,
  init,
  isFeatureEnabled,
  isFeatureInResultEnabled,
  localize,
  _selectElements: selectElements,
};



// WEBPACK FOOTER //
// ./static/js/v0/features.js